package com.exura.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.exura.components.dialogs.DialogContainer
import com.exura.components.dialogs.DialogController
import com.exura.components.dialogs.YesNoDialog
import com.exura.components.icons.UntiledIcon
import com.exura.components.layouts.nav.AccountDropdown
import com.exura.components.layouts.nav.NavNavigableElementWarnVariant
import com.exura.components.layouts.nav.Navigation
import com.exura.components.layouts.nav.NavigationButton
import com.exura.components.layouts.nav.NavigationLink
import com.exura.components.layouts.nav.NavigationSection
import com.exura.components.snackbar.Snackbar
import com.exura.features.localization.commonLocalization
import com.exura.features.newauth.domain.manager.AuthManager
import com.exura.features.newauth.presentation.HandleSession
import com.exura.features.userprofile.domain.model.ProviderProfile
import com.exura.features.userprofile.domain.usecase.GetProviderProfileUseCase
import com.exura.navigation.Route
import com.exura.navigation.navigateTo
import com.exura.ui.Assets
import com.exura.ui.Dimens
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import kotlinx.coroutines.launch


@Layout
@Composable
fun NavigableLayout(ctx: PageContext, content: @Composable NavigableLayoutScope.() -> Unit) {
    val authManager: AuthManager = injectAndRemember()
    val getProviderProfileUseCase = injectAndRemember<GetProviderProfileUseCase>()
    val dialogController: DialogController = remember { DialogController() }
    val snackbarEmitter = Snackbar()
    val scope = remember(authManager, dialogController, getProviderProfileUseCase, snackbarEmitter) {
        NavigableLayoutScope(
            authManager,
            dialogController,
            snackbarEmitter,
            getProviderProfileUseCase,
        )
    }

    val onLogout = {
        dialogController.show {
            YesNoDialog(
                title = commonLocalization.current.nav.confirmLogout,
                onDismiss = { dialogController.close() },
            ) {
                authManager.signOut()
                ctx.navigateTo(Route.LOGIN)
            }
        }
    }

    HandleSession(context = ctx)

    Box(
        modifier = Modifier.fillMaxSize(),
    ) {
        Row(
            modifier = Modifier
                .width(Dimens.appViewWidth)
                .height(Dimens.appViewHeight)
                .overflow(Overflow.Hidden)
        ) {
            scope.MainNavigation(onLogout = onLogout)

            Box(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .minWidth(Dimens.spacingNone)
                    .padding(leftRight = Dimens.pageIndent, topBottom = Dimens.pageWrapping)
            ) {
                scope.content()
            }
        }

        if (dialogController.stack.isNotEmpty()) {
            val topDialog = dialogController.stack.last()
            val dialogVariant = dialogController.variants.last()

            DialogContainer(
                onDismiss = { dialogController.close() },
                variant = dialogVariant,
                content = topDialog,
            )
        }
    }
}

@Composable
private fun NavigableLayoutScope.MainNavigation(
    onLogout: () -> Unit,
) {
    val profile by remember { observeProfile() }.collectAsState(initial = null)
    Navigation {
        Column(
            modifier = Modifier.fillMaxSize(),
            verticalArrangement = Arrangement.spacedBy(Dimens.navNavigationSectionsGap),
        ) {

            Column( // Rolling sections
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .minHeight(Dimens.spacingNone)
                    .overflow(Overflow.Auto),
                verticalArrangement = Arrangement.spacedBy(Dimens.navNavigationSectionsGapRolling),
            ) {

                NavigationSection(
                    title = commonLocalization.current.nav.templates,
                    icon = { UntiledIcon(Assets.IconsUntiled.NAV_ELEMENT_TEMPLATES) }
                ) {
                    NavigationLink(
                        path = Route.DASHBOARD,
                        text = commonLocalization.current.nav.library,
                    ) { UntiledIcon(Assets.IconsUntiled.GENERAL_LIST) }
                    NavigationLink(
                        path = Route.PLAN_CREATOR,
                        text = commonLocalization.current.nav.createTemplate,
                    ) { UntiledIcon(Assets.IconsUntiled.GENERAL_ADD) }
                    NavigationLink(
                        path = Route.IMPORT_PLANS,
                        text = commonLocalization.current.nav.importTemplate,
                    ) { UntiledIcon(Assets.IconsUntiled.GENERAL_DOWNLOAD) }
                    NavigationLink(
                        path = Route.PUBLIC_PLAN,
                        text = commonLocalization.current.nav.publicTemplate,
                    ) { UntiledIcon(Assets.IconsUntiled.NAV_ELEMENT_TEMPLATE_PUBLIC) }
                }

            }

            Spacer()

            NavigationSection(
                title = commonLocalization.current.nav.settings,
                icon = { UntiledIcon(Assets.IconsUntiled.NAV_ELEMENT_ACCOUNT) }
            ) {
                AccountDropdown(
                    profile = profile,
                ) {
                    NavigationLink(
                        path=Route.SETTINGS,
                        text=commonLocalization.current.nav.profile,
                    ) { UntiledIcon(Assets.IconsUntiled.NAV_ELEMENT_PROFILE) }
                    NavigationButton(
                        text = commonLocalization.current.nav.logout,
                        variant = NavNavigableElementWarnVariant,
                        onClick = onLogout,
                    ) { UntiledIcon(Assets.IconsUntiled.NAV_ELEMENT_LOGOUT) }
                }
            }

        }
    }
}
